import React, { useState, useEffect } from 'react';
import { X, Award, User, MapPin, CreditCard, Save } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const MarketingAgentEditModal = ({ isOpen, onClose, agent, onSuccess, defaultTab = 'profile' }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  // Marketing Profile
  const [region, setRegion] = useState('');
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');

  // Personal details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [alternativePhone, setAlternativePhone] = useState('');
  const [gender, setGender] = useState('Male');
  const [dob, setDob] = useState('');

  // Geographic Address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [country, setCountry] = useState('India');

  // Payout & Banking
  const [accountName, setAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [upiId, setUpiId] = useState('');

  // Synchronize the active tab when modal is opened
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab || 'profile');
    }
  }, [isOpen, defaultTab]);

  // Pre-populate fields when agent prop changes
  useEffect(() => {
    if (agent) {
      const onboarding = agent.user?.onboardingData || {};
      const banking = onboarding.bankDetails || onboarding.banking || {};

      setRegion(agent.region || '');
      setIdentificationNumber(agent.identificationNumber || onboarding.panNumber || onboarding.aadhaarNumber || '');
      setPanNumber(onboarding.panNumber || agent.panNumber || '');
      setAadhaarNumber(onboarding.aadhaarNumber || agent.aadhaarNumber || '');

      setFirstName(agent.user?.firstName || '');
      setLastName(agent.user?.lastName || '');
      setEmail(agent.user?.email || '');
      setPhone(agent.user?.phone || '');
      setAlternativePhone(agent.user?.alternativePhone || onboarding.alternativePhone || '');
      setGender(agent.user?.gender || onboarding.gender || 'Male');
      setDob(agent.user?.dob ? new Date(agent.user.dob).toISOString().split('T')[0] : (onboarding.dob ? new Date(onboarding.dob).toISOString().split('T')[0] : ''));

      setStreet(agent.user?.address?.street || onboarding.currentAddress || onboarding.street || '');
      setCity(agent.user?.address?.city || onboarding.city || '');
      setState(agent.user?.address?.state || onboarding.state || '');
      setPincode(agent.user?.address?.pincode || onboarding.pincode || '');
      setCountry(agent.user?.address?.country || 'India');

      setAccountName(banking.accountName || '');
      setBankName(banking.bankName || '');
      setAccountNumber(banking.accountNumber || '');
      setIfscCode(banking.ifscCode || '');
      setUpiId(banking.upiId || onboarding.upiId || '');
    }
  }, [agent, isOpen]);

  if (!isOpen || !agent) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        region,
        identificationNumber,
        firstName,
        lastName,
        email,
        phone,
        alternativePhone,
        gender,
        dob: dob || null,
        address: {
          street,
          city,
          state,
          pincode,
          country
        },
        onboardingData: {
          ...agent.user?.onboardingData,
          panNumber,
          aadhaarNumber,
          alternativePhone,
          gender,
          dob: dob || null,
          currentAddress: street,
          city,
          state,
          pincode,
          bankDetails: {
            accountName,
            bankName,
            accountNumber,
            ifscCode,
            upiId
          },
          upiId
        }
      };

      const response = await api.put(`/marketing-agents/${agent._id}`, payload);
      if (response.data) {
        toast.success('Lead Partner profile updated successfully!');
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Marketing Profile', icon: Award },
    { id: 'personal', label: 'Personal Details', icon: User },
    { id: 'address', label: 'Geographic Profile', icon: MapPin },
    { id: 'financial', label: 'Banking & Payout', icon: CreditCard }
  ];

  const ActiveIcon = tabs.find(t => t.id === activeTab)?.icon || Award;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <div className="bg-white w-full max-w-5xl h-[95vh] md:h-[90vh] overflow-hidden rounded-[32px] md:rounded-[40px] shadow-2xl flex flex-col relative animate-in fade-in zoom-in duration-300">
        
        {/* Header */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-b border-slate-100 flex items-center justify-between bg-white relative z-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-inner shrink-0">
              <ActiveIcon className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-black text-[#143E78] tracking-tight uppercase">
                Edit Lead Partner Profile
              </h2>
              <p className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
                Update marketing regions, personal details, address & payouts
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full transition-all text-slate-400">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="px-6 md:px-10 bg-slate-50 border-b border-slate-100 flex gap-2 overflow-x-auto py-2 shrink-0">
          {tabs.map(tab => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-[10px] md:text-xs font-black uppercase tracking-wider rounded-xl transition-all ${
                  isActive ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-200/50 hover:text-slate-700'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Form content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-10 md:py-8 space-y-6">
          
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Operating Region</label>
                  <input
                    type="text"
                    required
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Identification Number</label>
                  <input
                    type="text"
                    value={identificationNumber}
                    onChange={(e) => setIdentificationNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">PAN Number</label>
                  <input
                    type="text"
                    value={panNumber}
                    onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Aadhaar Number</label>
                  <input
                    type="text"
                    value={aadhaarNumber}
                    onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

          {/* PERSONAL TAB */}
          {activeTab === 'personal' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Phone Number</label>
                  <input
                    type="text"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Alternative Phone</label>
                  <input
                    type="text"
                    value={alternativePhone}
                    onChange={(e) => setAlternativePhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Gender</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Date of Birth</label>
                  <input
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all cursor-pointer"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ADDRESS TAB */}
          {activeTab === 'address' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Street Address</label>
                  <input
                    type="text"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">State</label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Pincode</label>
                  <input
                    type="text"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Country</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

          {/* FINANCIAL TAB */}
          {activeTab === 'financial' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2">Payout Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Account Name</label>
                  <input
                    type="text"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">UPI ID</label>
                  <input
                    type="text"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="e.g. name@upi"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              <h3 className="text-xs font-black text-[#143E78] uppercase tracking-widest border-b pb-2 mt-8">Bank Account details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Bank Name</label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">Account Number</label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-[#143E78] uppercase tracking-widest pl-1">IFSC Code</label>
                  <input
                    type="text"
                    value={ifscCode}
                    onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-800 text-sm outline-none focus:bg-white focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-[32px] md:rounded-b-[40px] shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 bg-[#143E78] text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-indigo-900 transition-all shadow-lg active:scale-95 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Updates'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default MarketingAgentEditModal;
